import { useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { Outlet } from 'react-router'
import { Link } from 'react-router-dom'
import type { ObjectProperties, Reservation } from '../../shared/types'
import { useObjectList } from '../api/object'
import { useSavedViewListAll } from '../api/saved-views'
import { Button, ButtonLink } from '../components/button'
import { ClientFormatter } from '../components/client-formatter'
import { DateFormatter } from '../components/date-formatter'
import { dateRangeToPrimitive, DateRange } from '../components/daterange-picker'
import { DueFormatter } from '../components/due-formatter'
import { LocationFormatter } from '../components/location-formatter'
import { Paper } from '../components/paper'
import { Sidepanel } from '../components/sidepanel'
import { SourceFormatter } from '../components/source-formatter'
import { VehicleFormatter } from '../components/vehicle-formatter'
import { useIgnoreRentner } from '../ignore-rentner'
import { FilteringSidepanel } from '../objects/filtering-sidepanel'
import { Views } from '../objects/views'
import { List, useList } from '../scaffolding/list'
import { ListHeader } from '../scaffolding/list-header'
import { STATUS, STATUS_STYLES } from './const'

export type ReservationParamsType = {
	id?: string
	vehicle_id?: Array<string | number>
	date_from?: DateRange
	date_to?: DateRange
	location_from?: Array<string | number>
	location_to?: Array<string | number>
	status?: Array<string | number>
} & ObjectProperties

const filterToPrimitive = ({ date_from, date_to, ...rest }: ReservationParamsType) => ({
	...rest,
	date_from: date_from ? dateRangeToPrimitive(date_from) : undefined,
	date_to: date_to ? dateRangeToPrimitive(date_to) : undefined,
})

export function Table() {
	const [params, setParams] = useList()
	const [filter, setFilter] = useState<ReservationParamsType & { viewId?: string }>({})
	const { viewId, ...rest } = filter
	const { data: savedViews = [] } = useSavedViewListAll('reservation')
	const view = savedViews.find(v => v.id === viewId)

	const ignoreRentner = useIgnoreRentner()
	const query = useObjectList('reservation', {
		...filterToPrimitive(rest),
		...params,
		ignoreRentner,
		withDue: true,
	})

	const onSetView = (viewId?: string, filter?: ReservationParamsType) => {
		setFilter({ ...filter, viewId } as ReservationParamsType & { viewId?: string })
	}

	return (
		<Paper>
			<h3 className="text-md font-bold hidden print:block p-2">{view?.name ?? ''}</h3>
			<div className="flex justify-between gap-4 px-5 pt-4 sm:px-6 sm:pt-5 print:hidden">
				<h3 className="text-md font-bold">Seznam rezervací:</h3>
				<DialogTrigger>
					<Button variant="SECONDARY">Filrovat</Button>
					<Sidepanel heading="Filtrovat">
						{({ close }) => (
							<FilteringSidepanel
								object="reservation"
								onClose={close}
								filter={filter}
								onSubmit={setFilter}
								viewId={viewId}
							/>
						)}
					</Sidepanel>
				</DialogTrigger>
			</div>
			<Views<ReservationParamsType>
				onSetView={onSetView}
				viewId={viewId}
				filter={rest}
				object="reservation"
			/>

			<List<Reservation>
				id="reservation"
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<ListHeader sortBy="id">ID</ListHeader>
						<th>Vozidlo</th>
						<ListHeader sortBy="date_from">Termín</ListHeader>
						<th>Pobočka [in/out]</th>
						<th>Klient</th>
						<th>Stav</th>
						<ListHeader sortBy="due">Dluh</ListHeader>
						<th>Zdroj</th>
						<th className="print:hidden"></th>
					</tr>
				}
			>
				{reservation => (
					<tr key={reservation.id}>
						<td width="8%">{reservation.id}</td>
						<td width="16%">
							<Link to={`/svc/vehicle/${reservation.vehicle_id}`}>
								<VehicleFormatter id={reservation.vehicle_id} />
							</Link>
						</td>
						<td width="16%">
							<DateFormatter date={reservation.date_from} format="d.M. HH:mm" />
							<br />
							<DateFormatter date={reservation.date_to} format="d.M. HH:mm" />
						</td>
						<td width="16%">
							<LocationFormatter id={reservation.location_from} />
							<br />
							<LocationFormatter id={reservation.location_to} />
						</td>
						<td width="16%">
							<Link to={`/svc/client/${reservation.client_id}`}>
								<ClientFormatter id={reservation.client_id} />
							</Link>
						</td>
						<td width="14%">
							<span
								className={`px-2 rounded-xl text-xxs font-semibold ${STATUS_STYLES[reservation.status]}`}
							>
								{STATUS[reservation.status]}
							</span>
						</td>
						<td>
							{reservation ? (
								<DueFormatter value={reservation.due as number} />
							) : null}
						</td>
						<td width="4%">
							<SourceFormatter source={reservation.source} />
						</td>
						<td className="print:hidden" width="10%">
							<ButtonLink variant="SECONDARY" size="SMALL" to={`${reservation.id}`}>
								Detail
							</ButtonLink>
						</td>
					</tr>
				)}
			</List>

			<Outlet />
		</Paper>
	)
}
